import { emptyChatCastingFilter, castingFilterLabel } from './ChatCastingSearchFields';
import BroadcastCastingFilterControl from './BroadcastCastingFilterControl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from '../Sidebar';
import {
  AvatarChipGridSkeleton,
  EventSearchResultSkeleton,
} from '../../common/ContentSkeletons';
import DsButton from '../../common/DsButton';
import StickyPageTopbar from '../../common/StickyPageTopbar';
import { useToast } from '../../common/ToastSystem';
import { PersonAvatar } from '../../common/avatar/PersonAvatar';
import { useAuth } from '../../../contexts/AuthContext';
import { resolveAdminPermission } from '../../../constants/adminPermissionCatalog';
import {
  getEventCastingOverview,
  listStageSeatingParticipantArtists,
  searchAdminBulkMessageEventCandidates,
  sendAdminBulkBatchMessages,
} from '../../../api/adminChat';
import { listAdminArtists, listArtistGroups, listParts } from '../../../api/artists';
import { formatItemCount } from '../../../utils/datetime';
import {
  BroadcastCheckboxModal,
  BroadcastConfirmModal,
  BroadcastEventSelectModal,
  BroadcastMessageDraftList,
} from './adminArtistBroadcastPageParts';
import {
  appendBroadcastCastingParams,
  BULK_BATCH_MAX_FILE_SIZE_BYTES,
  BULK_BATCH_MAX_FILE_SIZE_MB,
  BULK_BATCH_MAX_TOTAL_FILE_SIZE_BYTES,
  BULK_BATCH_MAX_TOTAL_FILE_SIZE_MB,
  BULK_LIMIT,
  BULK_MESSAGE_ITEM_LIMIT,
  EVENT_SEARCH_LIMIT,
  FILE_ACCEPT,
  IMAGE_ACCEPT,
  MESSAGE_LIMIT,
  PREFECTURE_OPTIONS,
  PREFECTURE_SET,
  buildEventOptionLabel,
  createBroadcastMessageDraft,
  getGenderToneClass,
  isPendingResponse,
  normalizeArtist,
  parseNumeric,
} from './adminArtistBroadcastPageUtils';
import '../../../css/admin/chats/AdminArtistBroadcastPage.css';
import DsSelect from '../../common/DsSelect';
import { normalizeChatImageForUpload } from '../../common/chat/composer/chatImageCompatibility';

function AdminArtistBroadcastPage() {
  const { user } = useAuth();
  const { success, error: toastError } = useToast();
  const adminRole = String(user?.role || 'general').trim().toLowerCase() || 'general';
  const adminPermissions = Array.isArray(user?.permissions)
    ? user.permissions
    : user?.permissions
      ? [user.permissions]
      : [];
  const canViewEventCatalog =
    resolveAdminPermission({
      role: adminRole,
      isExternalAccount: user?.is_external_account === true,
      permissions: adminPermissions,
      permissionKey: 'NAV_EVENTS',
    }) ||
    resolveAdminPermission({
      role: adminRole,
      isExternalAccount: user?.is_external_account === true,
      permissions: adminPermissions,
      permissionKey: 'EVENT_VIEW_PUBLISHED_LIST',
    }) ||
    resolveAdminPermission({
      role: adminRole,
      isExternalAccount: user?.is_external_account === true,
      permissions: adminPermissions,
      permissionKey: 'EVENT_VIEW_UNPUBLISHED_LIST',
    });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [parts, setParts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [artists, setArtists] = useState([]);
  const [artistPage, setArtistPage] = useState(1);
  const [artistTotalCount, setArtistTotalCount] = useState(0);
  const [artistHasMore, setArtistHasMore] = useState(false);
  const [artistLoadingMore, setArtistLoadingMore] = useState(false);
  const [castingFilter, setCastingFilter] = useState(emptyChatCastingFilter);
  const [keyword, setKeyword] = useState('');
  const [accountType, setAccountType] = useState('all');
  const [gender, setGender] = useState('all');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [includeSubAreas, setIncludeSubAreas] = useState(false);
  const [requireDayTrip, setRequireDayTrip] = useState(false);
  const [requireOvernight, setRequireOvernight] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEventSummary, setSelectedEventSummary] = useState(null);
  const [eventScopeMode, setEventScopeMode] = useState('participants');
  const [eventParticipants, setEventParticipants] = useState(new Set());
  const [eventUnanswered, setEventUnanswered] = useState(new Set());
  const [eventScopeLoading, setEventScopeLoading] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventSearchKeyword, setEventSearchKeyword] = useState('');
  const [eventSearchStartDate, setEventSearchStartDate] = useState('');
  const [eventSearchEndDate, setEventSearchEndDate] = useState('');
  const [eventSearchResults, setEventSearchResults] = useState([]);
  const [eventSearchLoading, setEventSearchLoading] = useState(false);
  const [eventSearchInitialized, setEventSearchInitialized] = useState(false);
  const [eventSearchHasMore, setEventSearchHasMore] = useState(false);
  const [selectedArtistIds, setSelectedArtistIds] = useState([]);
  const [selectedArtistMeta, setSelectedArtistMeta] = useState({});
  const [messageDrafts, setMessageDrafts] = useState([createBroadcastMessageDraft()]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPartModal, setShowPartModal] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const artistLoadMoreInFlightRef = useRef(false);
  const draftFileSelectionVersionRef = useRef(new Map());
  const artistQueryKey = useMemo(
    () =>
      JSON.stringify({
        castingFilter,
        keyword: keyword.trim(),
        accountType,
        gender,
        ageMin,
        ageMax,
        selectedGroupId,
        selectedParts: [...selectedParts],
        selectedAreas: [...selectedAreas],
        includeSubAreas,
        requireDayTrip,
        requireOvernight,
      }),
    [
      castingFilter,
      keyword,
      accountType,
      gender,
      ageMin,
      ageMax,
      selectedGroupId,
      selectedParts,
      selectedAreas,
      includeSubAreas,
      requireDayTrip,
      requireOvernight,
    ],
  );
  const artistQueryKeyRef = useRef(artistQueryKey);

  const buildArtistSearchParams = useCallback(
    (pageNumber = 1) => {
      const params = new URLSearchParams();
      params.set('page', String(pageNumber));
      params.set('limit', String(BULK_LIMIT));
      params.set('sort', 'login');
      if (keyword.trim()) params.set('name', keyword.trim());
      if (accountType !== 'all') params.set('accountType', accountType);
      if (gender !== 'all') params.set('gender', gender);
      params.set('preReg', 'hide');
      if (selectedGroupId) params.set('groupId', selectedGroupId);
      if (selectedParts.length) params.set('parts', selectedParts.join(','));
      if (selectedAreas.length) params.set('areas', selectedAreas.join(','));
      if (includeSubAreas) params.set('includeSub', 'true');
      if (requireDayTrip) params.set('dayTrip', 'true');
      if (requireOvernight) params.set('overnight', 'true');
      const min = parseNumeric(ageMin);
      const max = parseNumeric(ageMax);
      if (min !== null) params.set('ageMin', String(min));
      if (max !== null) params.set('ageMax', String(max));
      appendBroadcastCastingParams(params, castingFilter);
      return params;
    },
    [
      castingFilter,
      keyword,
      accountType,
      gender,
      ageMin,
      ageMax,
      selectedGroupId,
      selectedParts,
      selectedAreas,
      includeSubAreas,
      requireDayTrip,
      requireOvernight,
    ],
  );

  useEffect(() => {
    artistQueryKeyRef.current = artistQueryKey;
  }, [artistQueryKey]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [partsData, groupsData] = await Promise.all([
          listParts(),
          listArtistGroups(),
        ]);
        if (!mounted) return;
        setParts(Array.isArray(partsData) ? [...partsData].sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0)) : []);
        setGroups(Array.isArray(groupsData) ? groupsData : []);
      } catch (e) {
        if (mounted && e?.name !== 'AbortError') toastError('初期データの取得に失敗しました');
      }
    })();
    return () => {
      mounted = false;
    };
  }, [toastError]);

  useEffect(() => {
    let mounted = true;
    artistLoadMoreInFlightRef.current = false;
    setArtistLoadingMore(false);
    setArtists([]); setArtistHasMore(false); setArtistTotalCount(0);
    (async () => {
      setLoading(true);
      try {
        const params = buildArtistSearchParams(1);
        const data = await listAdminArtists(Object.fromEntries(params.entries()));
        if (!mounted) return;
        const normalized = (Array.isArray(data?.artists) ? data.artists : [])
          .map(normalizeArtist)
          .filter((artist) => artist.is_registered);
        const totalCount = Number(data?.totalCount ?? data?.paging?.totalCount ?? normalized.length) || 0;
        setArtists(normalized);
        setArtistPage(1);
        setArtistTotalCount(totalCount);
        setArtistHasMore(normalized.length > 0 && normalized.length < totalCount);
        setSelectedArtistMeta((prev) => {
          const next = { ...prev };
          normalized.forEach((a) => {
            if (a.artist_id) next[a.artist_id] = a;
          });
          return next;
        });
      } catch (e) {
        if (mounted && e?.name !== 'AbortError') toastError('アーティスト候補の取得に失敗しました');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [artistQueryKey, buildArtistSearchParams, toastError]);

  const loadMoreArtists = async () => {
    if (loading || artistLoadingMore || !artistHasMore) return;
    if (artistLoadMoreInFlightRef.current) return;
    const queryKeyAtStart = artistQueryKeyRef.current;
    artistLoadMoreInFlightRef.current = true;
    setArtistLoadingMore(true);
    try {
      const nextPage = artistPage + 1;
      const params = buildArtistSearchParams(nextPage);
      const data = await listAdminArtists(Object.fromEntries(params.entries()));
      if (artistQueryKeyRef.current !== queryKeyAtStart) return;
      const normalized = (Array.isArray(data?.artists) ? data.artists : [])
        .map(normalizeArtist)
        .filter((artist) => artist.is_registered);
      const totalCount = Number(data?.totalCount ?? data?.paging?.totalCount ?? artistTotalCount) || 0;
      setArtists((prev) => {
        const seen = new Set(prev.map((a) => a.artist_id));
        const merged = [...prev];
        normalized.forEach((a) => {
          if (!a?.artist_id || seen.has(a.artist_id)) return;
          seen.add(a.artist_id);
          merged.push(a);
        });
        return merged;
      });
      setSelectedArtistMeta((prev) => {
        const next = { ...prev };
        normalized.forEach((a) => {
          if (a.artist_id) next[a.artist_id] = a;
        });
        return next;
      });
      setArtistPage(nextPage);
      setArtistTotalCount(totalCount);
      setArtistHasMore(normalized.length > 0 && nextPage * BULK_LIMIT < totalCount);
    } catch (e) {
      if (artistQueryKeyRef.current !== queryKeyAtStart) return;
      toastError('追加アーティスト候補の取得に失敗しました');
    } finally {
      if (artistQueryKeyRef.current === queryKeyAtStart) {
        setArtistLoadingMore(false);
      }
      if (artistQueryKeyRef.current === queryKeyAtStart) artistLoadMoreInFlightRef.current = false;
    }
  };

  const handleArtistListScroll = (e) => {
    if (loading || artistLoadingMore || !artistHasMore) return;
    const el = e?.currentTarget;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 64) {
      loadMoreArtists();
    }
  };

  useEffect(() => {
    if (!canViewEventCatalog || !selectedEventId) {
      setEventParticipants(new Set());
      setEventUnanswered(new Set());
      return;
    }
    let mounted = true;
    (async () => {
      setEventScopeLoading(true);
      try {
        const [pData, oData] = await Promise.all([
          listStageSeatingParticipantArtists(selectedEventId),
          getEventCastingOverview(selectedEventId),
        ]);
        if (!mounted) return;
        setEventParticipants(new Set((Array.isArray(pData?.items) ? pData.items : []).map((i) => String(i?.artist_id || '').trim()).filter(Boolean)));
        setEventUnanswered(new Set((Array.isArray(oData?.items) ? oData.items : []).filter(isPendingResponse).map((i) => String(i?.artist_id || '').trim()).filter(Boolean)));
      } catch (e) {
        if (mounted && e?.name !== 'AbortError') toastError('イベント条件の候補取得に失敗しました');
      } finally {
        if (mounted) setEventScopeLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [canViewEventCatalog, selectedEventId, toastError]);

  useEffect(() => {
    if (!selectedEventId && eventScopeMode !== 'participants') setEventScopeMode('participants');
  }, [selectedEventId, eventScopeMode]);

  const eventScopedArtistIds = useMemo(() => {
    if (!selectedEventId) return null;
    return eventScopeMode === 'unanswered' ? eventUnanswered : eventParticipants;
  }, [selectedEventId, eventScopeMode, eventParticipants, eventUnanswered]);
  const displayedArtists = useMemo(() => (!eventScopedArtistIds ? artists : artists.filter((a) => eventScopedArtistIds.has(a.artist_id))), [artists, eventScopedArtistIds]);
  const selectedArtistIdSet = useMemo(() => new Set(selectedArtistIds), [selectedArtistIds]);
  const selectedCount = selectedArtistIds.length;
  const sendableDisplayedArtists = useMemo(() => displayedArtists, [displayedArtists]);
  const previewSelectedArtists = useMemo(() => selectedArtistIds.map((id) => selectedArtistMeta[id]).filter(Boolean), [selectedArtistIds, selectedArtistMeta]);
  const selectedPartLabels = useMemo(() => {
    const map = new Map(parts.map((p) => [String(p?.value || ''), String(p?.label || p?.value || '')]));
    return selectedParts.map((v) => map.get(String(v)) || String(v));
  }, [parts, selectedParts]);
  const selectedAreaLabels = useMemo(() => {
    const map = new Map(PREFECTURE_OPTIONS.map((p) => [p.value, p.label]));
    return selectedAreas.map((v) => map.get(String(v)) || String(v));
  }, [selectedAreas]);
  const selectedEventLabel = useMemo(() => {
    if (!selectedEventId) return '';
    if (selectedEventSummary?.event_uuid && String(selectedEventSummary.event_uuid) === String(selectedEventId)) {
      return buildEventOptionLabel(selectedEventSummary);
    }
    const matched = eventSearchResults.find((event) => String(event?.event_uuid || '') === String(selectedEventId));
    if (matched) return buildEventOptionLabel(matched);
    return String(selectedEventId);
  }, [eventSearchResults, selectedEventId, selectedEventSummary]);
  const textCharCount = useMemo(
    () => messageDrafts.reduce((sum, draft) => sum + (draft.type === 'text' ? String(draft.content || '').length : 0), 0),
    [messageDrafts],
  );

  const searchEventCandidates = async () => {
    if (!canViewEventCatalog) {
      setEventSearchResults([]);
      setEventSearchHasMore(false);
      setEventSearchInitialized(true);
      return;
    }
    if (
      eventSearchStartDate &&
      eventSearchEndDate &&
      String(eventSearchStartDate) > String(eventSearchEndDate)
    ) {
      toastError('イベント検索の期間指定が不正です（開始日 <= 終了日）');
      return;
    }

    setEventSearchLoading(true);
    try {
      const params = new URLSearchParams();
      const keywordTrimmed = String(eventSearchKeyword || '').trim();
      if (keywordTrimmed) params.set('q', keywordTrimmed);
      if (eventSearchStartDate) params.set('startDate', eventSearchStartDate);
      if (eventSearchEndDate) params.set('endDate', eventSearchEndDate);
      params.set('limit', String(EVENT_SEARCH_LIMIT));
      const data = await searchAdminBulkMessageEventCandidates(Object.fromEntries(params.entries()));
      setEventSearchResults(Array.isArray(data?.events) ? data.events : []);
      setEventSearchHasMore(Boolean(data?.hasMore));
      setEventSearchInitialized(true);
    } catch (e) {
      toastError(e?.message || 'イベント検索に失敗しました');
    } finally {
      setEventSearchLoading(false);
    }
  };

  const openEventSelectModal = () => {
    if (!canViewEventCatalog) return;
    setShowEventModal(true);
    if (!eventSearchInitialized && !eventSearchLoading) {
      searchEventCandidates();
    }
  };

  const handleSelectEvent = (event) => {
    const eventUuid = String(event?.event_uuid || '').trim();
    if (!eventUuid) return;
    setSelectedEventId(eventUuid);
    setSelectedEventSummary(event);
    setShowEventModal(false);
  };

  const clearSelectedEvent = () => {
    setSelectedEventId('');
    setSelectedEventSummary(null);
    setEventParticipants(new Set());
    setEventUnanswered(new Set());
  };

  const togglePart = (value) => setSelectedParts((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  const toggleArea = (value) => {
    if (!PREFECTURE_SET.has(value)) return;
    setSelectedAreas((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };
  const addMessageDraft = () => {
    setMessageDrafts((prev) => {
      if (prev.length >= BULK_MESSAGE_ITEM_LIMIT) return prev;
      return [...prev, createBroadcastMessageDraft()];
    });
  };
  const removeMessageDraft = (id) => {
    setMessageDrafts((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((draft) => draft.id !== id);
    });
  };
  const updateMessageDraftType = (id, type) => {
    setMessageDrafts((prev) =>
      prev.map((draft) =>
        draft.id === id
          ? {
              ...draft,
              type,
              content: '',
              file: null,
            }
          : draft,
      ),
    );
  };
  const updateMessageDraftContent = (id, content) => {
    setMessageDrafts((prev) =>
      prev.map((draft) =>
        draft.id === id
          ? {
              ...draft,
              content: String(content || ''),
            }
          : draft,
      ),
    );
  };
  const updateMessageDraftFile = async (id, file, type = 'file') => {
    const selectionVersion = (draftFileSelectionVersionRef.current.get(id) || 0) + 1;
    draftFileSelectionVersionRef.current.set(id, selectionVersion);
    if (file && Number(file.size || 0) > BULK_BATCH_MAX_FILE_SIZE_BYTES) {
      toastError(`添付ファイルは1件あたり${BULK_BATCH_MAX_FILE_SIZE_MB}MB以内で選択してください`);
      return;
    }
    let compatibleFile = file || null;
    if (compatibleFile && type === 'image') {
      try {
        compatibleFile = await normalizeChatImageForUpload(compatibleFile);
      } catch (error) {
        toastError(error?.message || '画像を送信できる形式へ変換できませんでした');
        return;
      }
    }
    if (draftFileSelectionVersionRef.current.get(id) !== selectionVersion) return;
    setMessageDrafts((prev) =>
      prev.map((draft) =>
        draft.id === id
          ? {
              ...draft,
              file: compatibleFile,
            }
          : draft,
      ),
    );
  };
  const addArtistSelection = (artist) => {
    if (!artist?.artist_id) return;
    setSelectedArtistMeta((prev) => ({ ...prev, [artist.artist_id]: artist }));
    setSelectedArtistIds((prev) => {
      if (prev.includes(artist.artist_id)) return prev;
      if (prev.length >= BULK_LIMIT) return prev;
      return [...prev, artist.artist_id];
    });
  };
  const removeArtistSelection = (artistId) => setSelectedArtistIds((prev) => prev.filter((id) => id !== artistId));
  const addAllDisplayed = () => {
    setSelectedArtistMeta((prev) => {
      const next = { ...prev };
      sendableDisplayedArtists.forEach((a) => {
        next[a.artist_id] = a;
      });
      return next;
    });
    setSelectedArtistIds((prev) => {
      const next = Array.from(new Set([...prev, ...sendableDisplayedArtists.map((a) => a.artist_id)]));
      return next.slice(0, BULK_LIMIT);
    });
  };
  const clearSelections = () => setSelectedArtistIds([]);
  const openConfirmModal = () => {
    if (!selectedCount) return toastError('送信先アーティストを1名以上選択してください');
    if (!Array.isArray(messageDrafts) || messageDrafts.length === 0) {
      return toastError('送信メッセージを1件以上作成してください');
    }
    if (messageDrafts.length > BULK_MESSAGE_ITEM_LIMIT) {
      return toastError(`送信メッセージは最大${BULK_MESSAGE_ITEM_LIMIT}件です`);
    }
    for (let i = 0; i < messageDrafts.length; i += 1) {
      const draft = messageDrafts[i] || {};
      const type = String(draft.type || '').trim();
      if (!['text', 'image', 'file'].includes(type)) {
        return toastError(`送信メッセージ${i + 1}の種別が不正です`);
      }
      if (type === 'text') {
        const content = String(draft.content || '').trim();
        if (!content) return toastError(`送信メッセージ${i + 1}の本文を入力してください`);
        if (content.length > MESSAGE_LIMIT) {
          return toastError(`送信メッセージ${i + 1}の本文は${MESSAGE_LIMIT}文字以内で入力してください`);
        }
      } else if (!draft.file) {
        return toastError(`送信メッセージ${i + 1}の添付ファイルを選択してください`);
      }
    }
    const totalFileBytes = messageDrafts.reduce(
      (sum, draft) => sum + Number(draft?.file?.size || 0),
      0,
    );
    if (totalFileBytes > BULK_BATCH_MAX_TOTAL_FILE_SIZE_BYTES) {
      return toastError(`添付ファイル合計は${BULK_BATCH_MAX_TOTAL_FILE_SIZE_MB}MB以内で設定してください`);
    }
    setShowConfirmModal(true);
  };
  const submitBulkSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const key = `bulk-batch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const formData = new FormData();
      formData.append('artistIds', JSON.stringify(selectedArtistIds));
      const serializableItems = messageDrafts.map((draft) => {
        const type = String(draft.type || '').trim();
        if (type === 'text') {
          return {
            type: 'text',
            content: String(draft.content || ''),
          };
        }
        const fileField = `asset_${draft.id}`;
        if (draft.file) {
          formData.append(fileField, draft.file);
        }
        return {
          type,
          fileField,
        };
      });
      formData.append('items', JSON.stringify(serializableItems));

      const payload = await sendAdminBulkBatchMessages(formData, key);
      if (payload?.success === false) throw new Error(payload?.message || '一斉送信に失敗しました');
      success(`一斉送信を実行しました（対象: ${Number(payload?.sentCount || 0)}名 / メッセージ: ${Number(payload?.sentMessageCount || 0)}件）`);
      setShowConfirmModal(false);
      setSelectedArtistIds([]);
      setMessageDrafts([createBroadcastMessageDraft()]);
    } catch (e) {
      toastError(e?.message || '一斉送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <Sidebar />
      <div className="app-main admin-broadcast-page">
        <StickyPageTopbar title="一斉送信" />
        <div className="page-topbar-body">
        <section className="admin-broadcast-section">
          <h3>Step1. 送信先アーティスト選択</h3>
          <div className="admin-broadcast-filters">
            <label>キーワード検索<input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="名前 / メール / ひとこと / プロフィール / 管理者メモ" /></label>
            <label>アカウント種別<DsSelect value={accountType} onChange={(e) => setAccountType(e.target.value)}><option value="all">すべて</option><option value="solo">ソロ</option><option value="group">グループ</option></DsSelect></label>
            <label>性別<DsSelect value={gender} onChange={(e) => setGender(e.target.value)}><option value="all">すべて</option><option value="male">男性</option><option value="female">女性</option><option value="unknown">未設定</option></DsSelect></label>
            <label>年齢下限<input type="number" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} placeholder="指定なし" min="0" /></label>
            <label>年齢上限<input type="number" value={ageMax} onChange={(e) => setAgeMax(e.target.value)} placeholder="指定なし" min="0" /></label>
            <label>グループ<DsSelect value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)}><option value="">すべて</option>{groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.group_name}</option>)}</DsSelect></label>
          </div>
          <div className="admin-broadcast-filter-pickers">
            <BroadcastCastingFilterControl value={castingFilter} onChange={setCastingFilter} />
            <DsButton variant="secondary" size="sm" onClick={() => setShowPartModal(true)}>パート選択 {selectedParts.length ? `(${selectedParts.length})` : '(未選択)'}</DsButton>
            <DsButton variant="secondary" size="sm" onClick={() => setShowAreaModal(true)}>活動エリア選択 {selectedAreas.length ? `(${selectedAreas.length})` : '(未選択)'}</DsButton>
            <DsButton variant="secondary" size="sm" onClick={openEventSelectModal}>
              イベント参加条件 {selectedEventId ? '(選択中)' : '(未選択)'}
            </DsButton>
            {selectedEventId && <label className="admin-broadcast-event-scope">参加条件<DsSelect value={eventScopeMode} onChange={(e) => setEventScopeMode(e.target.value)}><option value="participants">契約参加者</option><option value="unanswered">未回答アーティストのみ</option></DsSelect></label>}
            {selectedEventId && (
              <DsButton variant="secondary" size="sm" onClick={clearSelectedEvent}>
                イベント選択を解除
              </DsButton>
            )}
          </div>
          <div className="admin-broadcast-selected-filter-labels">
            {castingFilter.eventId ? <span className="admin-broadcast-filter-label">{castingFilterLabel(castingFilter)}</span> : null}
            {selectedPartLabels.slice(0, 8).map((label) => <span key={`part-${label}`} className="admin-broadcast-filter-label"># {label}</span>)}
            {selectedAreaLabels.slice(0, 8).map((label) => <span key={`area-${label}`} className="admin-broadcast-filter-label">@ {label}</span>)}
            {selectedEventId && <span className="admin-broadcast-filter-label">@ イベント: {selectedEventLabel}</span>}
          </div>
          <div className="admin-broadcast-toggle-row">
            <label><input type="checkbox" checked={includeSubAreas} onChange={(e) => setIncludeSubAreas(e.target.checked)} />サブ活動エリアを含む</label>
            <label><input type="checkbox" checked={requireDayTrip} onChange={(e) => setRequireDayTrip(e.target.checked)} />日帰り出張対応可のみ</label>
            <label><input type="checkbox" checked={requireOvernight} onChange={(e) => setRequireOvernight(e.target.checked)} />宿泊出張対応可のみ</label>
          </div>
          <div className="admin-broadcast-actions">
            <DsButton onClick={addAllDisplayed} disabled={sending || loading}>表示アーティストまとめて追加</DsButton>
            <DsButton variant="secondary" onClick={clearSelections} disabled={sending || selectedCount === 0}>選択をすべて解除</DsButton>
            <div className="admin-broadcast-counts"><span>候補: {formatItemCount(displayedArtists.length)}</span><span>選択: {formatItemCount(selectedCount)}</span><span>取得: {formatItemCount(artistTotalCount || artists.length)}</span>{selectedEventId && <span>{eventScopeMode === 'unanswered' ? '未回答' : '契約参加者'}: {formatItemCount(eventScopeMode === 'unanswered' ? eventUnanswered.size : eventParticipants.size)}</span>}</div>
          </div>
          <p className="admin-broadcast-order-note">取得順: ログイン順（新しい順）</p>
          {eventScopeLoading && <EventSearchResultSkeleton rows={3} />}
          {loading ? (
            <div className="admin-broadcast-artist-list" role="status" aria-live="polite">
              <AvatarChipGridSkeleton className="admin-broadcast-artist-grid" rows={12} />
            </div>
          ) : (
            <div className="admin-broadcast-artist-list" onScroll={handleArtistListScroll}>
              {displayedArtists.length === 0 ? <p className="admin-broadcast-empty">条件に一致するアーティストがいません。</p> : (
                <div className="admin-broadcast-artist-grid">
                  {displayedArtists.map((artist) => {
                    const checked = selectedArtistIdSet.has(artist.artist_id);
                    const genderToneClass = getGenderToneClass(artist.gender);
                    return (
                      <button key={artist.artist_id} type="button" className={`admin-broadcast-artist-chip ${genderToneClass} casting-tone--${artist.casting_status_tone || 'none'}${checked ? ' is-selected' : ''}`} disabled={sending} onClick={() => checked ? removeArtistSelection(artist.artist_id) : addArtistSelection(artist)} title={artist.name || artist.artist_id}>
                        <PersonAvatar
                          role="artist"
                          gender={artist.gender}
                          name={artist.name || artist.artist_id}
                          profilePicture={artist.profile_picture}
                          variant="proxy"
                          size={30}
                          className="admin-broadcast-artist-avatar"
                        />
                        <span className="admin-broadcast-artist-chip-name">{artist.name || '(名前未設定)'}</span>
                        {checked && <span className="admin-broadcast-artist-chip-check">✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
              {artistLoadingMore && <p className="admin-broadcast-scroll-hint">さらに {BULK_LIMIT} 件を読み込み中...</p>}
              {!artistLoadingMore && artistHasMore && <p className="admin-broadcast-scroll-hint">一番下までスクロールすると、さらに {BULK_LIMIT} 件を読み込みます。</p>}
              {!artistLoadingMore && !artistHasMore && artists.length > 0 && <p className="admin-broadcast-scroll-hint">すべて読み込み済みです。</p>}
            </div>
          )}
        </section>
        <section className="admin-broadcast-section">
          <h3>Step2. 送信メッセージ内容作成</h3>
          <BroadcastMessageDraftList
            messageDrafts={messageDrafts}
            sending={sending}
            messageLimit={MESSAGE_LIMIT}
            imageAccept={IMAGE_ACCEPT}
            fileAccept={FILE_ACCEPT}
            maxItems={BULK_MESSAGE_ITEM_LIMIT}
            maxFileSizeMb={BULK_BATCH_MAX_FILE_SIZE_MB}
            maxTotalFileSizeMb={BULK_BATCH_MAX_TOTAL_FILE_SIZE_MB}
            updateMessageDraftType={updateMessageDraftType}
            updateMessageDraftContent={updateMessageDraftContent}
            updateMessageDraftFile={updateMessageDraftFile}
            removeMessageDraft={removeMessageDraft}
            addMessageDraft={addMessageDraft}
          />
          <div className="admin-broadcast-message-meta">
            <span>送信先: {selectedCount}件</span>
            <span>メッセージ件数: {messageDrafts.length}/{BULK_MESSAGE_ITEM_LIMIT}</span>
            <span>テキスト文字数合計: {textCharCount}</span>
          </div>
          <DsButton className="admin-broadcast-open-confirm" onClick={openConfirmModal} disabled={sending}>Step3. 確認モーダルへ進む</DsButton>
        </section>
        {selectedCount > 0 && (
          <section className="admin-broadcast-section">
            <h3>選択済みアーティスト</h3>
            <div className="admin-broadcast-selected-chip-list">
              {previewSelectedArtists.map((artist) => {
                const genderToneClass = getGenderToneClass(artist.gender);
                return (
                  <div key={artist.artist_id} className={`admin-broadcast-selected-chip ${genderToneClass}`}>
                    <PersonAvatar
                      role="artist"
                      gender={artist.gender}
                      name={artist.name || artist.artist_id}
                      profilePicture={artist.profile_picture}
                      variant="proxy"
                      size={30}
                      className="admin-broadcast-selected-avatar"
                    />
                    <span className="admin-broadcast-selected-chip-name">{artist.name || artist.artist_id}</span>
                    <DsButton
                      variant="danger"
                      size="sm"
                      icon
                      className="admin-broadcast-selected-chip-remove"
                      aria-label={`${artist.name || artist.artist_id}を選択から外す`}
                      onClick={() => removeArtistSelection(artist.artist_id)}
                      disabled={sending}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </DsButton>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        </div>
      </div>
      <BroadcastEventSelectModal
        open={showEventModal}
        onClose={() => setShowEventModal(false)}
        onClearSelectedEvent={clearSelectedEvent}
        eventSearchKeyword={eventSearchKeyword}
        setEventSearchKeyword={setEventSearchKeyword}
        eventSearchStartDate={eventSearchStartDate}
        setEventSearchStartDate={setEventSearchStartDate}
        eventSearchEndDate={eventSearchEndDate}
        setEventSearchEndDate={setEventSearchEndDate}
        searchEventCandidates={searchEventCandidates}
        eventSearchLoading={eventSearchLoading}
        eventSearchLimit={EVENT_SEARCH_LIMIT}
        eventSearchHasMore={eventSearchHasMore}
        eventSearchResults={eventSearchResults}
        selectedEventId={selectedEventId}
        buildEventOptionLabel={buildEventOptionLabel}
        handleSelectEvent={handleSelectEvent}
        loadingFallback={<EventSearchResultSkeleton rows={5} />}
      />
      <BroadcastCheckboxModal
        open={showPartModal}
        onClose={() => setShowPartModal(false)}
        title="パート選択"
        clearLabel="パート選択をクリア"
        onClear={() => setSelectedParts([])}
        options={parts}
        selectedValues={selectedParts}
        onToggle={togglePart}
      />
      <BroadcastCheckboxModal
        open={showAreaModal}
        onClose={() => setShowAreaModal(false)}
        title="活動エリア選択"
        clearLabel="活動エリア選択をクリア"
        onClear={() => setSelectedAreas([])}
        options={PREFECTURE_OPTIONS}
        selectedValues={selectedAreas}
        onToggle={toggleArea}
      />
      <BroadcastConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        sending={sending}
        onSubmit={submitBulkSend}
        selectedCount={selectedCount}
        messageDrafts={messageDrafts}
      />
    </div>
  );
}

export default AdminArtistBroadcastPage;
